import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';
import { GOOGLE_SIGN_IN_ENABLED } from '@/lib/authProviders';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, isLoading: authLoading } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const t = {
    bg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-[#F5F5F7]' : 'text-neutral-900',
    textSecondary: isDark ? 'text-[#A1A1A6]' : 'text-neutral-500',
    textMuted: isDark ? 'text-[#6E6E73]' : 'text-neutral-400',
    textFaint: isDark ? 'text-[#48484F]' : 'text-neutral-300',
    cardBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    cardBorder: isDark ? 'border-white/[0.06]' : 'border-neutral-200/70',
    inputBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-50',
    inputBorder: isDark ? 'border-white/[0.08]' : 'border-neutral-200',
    inputFocus: isDark ? 'focus:border-white/20 focus:bg-white/[0.06]' : 'focus:border-neutral-300 focus:bg-white',
    placeholder: isDark ? 'placeholder-[#6E6E73]' : 'placeholder-neutral-400',
    errorBg: isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600',
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !password.trim()) return;
      setIsSubmitting(true);
      setError('');

      const { error: signInError } = await signIn(email.trim(), password);
      if (signInError) {
        setError(signInError.message || 'Invalid email or password. Please try again.');
        setIsSubmitting(false);
        return;
      }

      navigate('/');
    },
    [email, password, signIn, navigate]
  );

  const handleGoogleSignIn = useCallback(async () => {
    setIsSubmitting(true);
    setError('');
    const { error: oauthError } = await signInWithGoogle();
    if (oauthError) {
      setError(oauthError.message || 'Google sign-in failed. Please try again.');
      setIsSubmitting(false);
    }
    // OAuth redirects away, no navigation needed
  }, [signInWithGoogle]);

  return (
    <div className={`study-shell study-auth min-h-[100dvh] flex items-center justify-center ${t.bg} px-5 transition-colors duration-500`} data-theme={resolvedTheme}>
      <motion.div
        className="w-full max-w-[380px]"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-neutral-900 flex items-center justify-center mb-4">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-sparkling-line text-white text-xl" />
            </div>
          </div>
          <h1 className={`text-xl font-bold ${t.textPrimary}`}>Welcome Back</h1>
          <p className={`text-sm ${t.textSecondary} mt-1`}>Log in to continue your study journey</p>
        </div>

        {/* Google Sign In */}
        {GOOGLE_SIGN_IN_ENABLED && <>
        <Pressable
          onClick={handleGoogleSignIn}
          disabled={isSubmitting}
          className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl border ${t.cardBorder} ${isDark ? 'bg-white/[0.03] text-white' : 'bg-white text-neutral-900'} text-sm font-medium mb-4 transition-colors`}
          scale={0.97}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          </div>
          <span className="whitespace-nowrap">Continue with Google</span>
        </Pressable>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-200'}`} />
          <span className={`text-[11px] ${t.textMuted}`}>or log in with email</span>
          <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-200'}`} />
        </div>
        </>}

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-xs mb-4 ${t.errorBg}`}
          >
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-sm" />
            </div>
            {error}
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div>
            <label className={`block text-xs font-medium ${t.textSecondary} mb-1.5`}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="student@school.edu"
              autoComplete="email"
              required
              className={`w-full ${t.inputBg} border ${t.inputBorder} ${t.inputFocus} rounded-xl px-4 py-3 text-sm ${t.textPrimary} ${t.placeholder} outline-none transition-all duration-200`}
            />
          </div>

          <div>
            <label className={`block text-xs font-medium ${t.textSecondary} mb-1.5`}>Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                required
                className={`w-full ${t.inputBg} border ${t.inputBorder} ${t.inputFocus} rounded-xl px-4 py-3 text-sm ${t.textPrimary} ${t.placeholder} outline-none transition-all duration-200 pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center ${t.textMuted} cursor-pointer`}
              >
                <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-3.5 h-3.5 rounded accent-neutral-900 cursor-pointer" />
              <span className={`text-xs ${t.textMuted}`}>Remember me</span>
            </label>
            <button
              type="button"
              onClick={() => navigate('/auth/forgot-password')}
              className={`text-xs font-medium ${t.textSecondary} hover:underline cursor-pointer`}
            >
              Forgot password?
            </button>
          </div>

          <Pressable
            type="submit"
            disabled={isSubmitting || authLoading || !email.trim() || !password.trim()}
            className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
            scale={0.97}
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-login-box-line text-sm" />
              </div>
            )}
            <span className="whitespace-nowrap">{isSubmitting ? 'Logging in...' : 'Log In'}</span>
          </Pressable>
        </form>

        {/* Sign Up Link */}
        <p className={`text-center text-sm ${t.textMuted} mt-6`}>
          No account yet?{' '}
          <Link
            to="/auth/signup"
            className={`font-semibold ${isDark ? 'text-white' : 'text-neutral-900'} hover:underline`}
          >
            Sign Up Free
          </Link>
        </p>

        {/* Guest continue */}
        <p className={`text-center text-xs ${t.textFaint} mt-4`}>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="hover:underline cursor-pointer"
          >
            Continue as guest
          </button>
        </p>
      </motion.div>
    </div>
  );
}